import { addTypedProvider } from '../../metaData';
import { MetadataModules } from '../../enums';
function base64ToUint8Array(base64) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}
function convertBuffer(dataSrc, numberOfChannels, numberOfSamples, bits, type) {
    const data = new Uint8Array(dataSrc);
    if (bits === 16 && type === 'SS') {
        const ret = [];
        const bytesPerSample = 2;
        const totalBytes = bytesPerSample * numberOfChannels * numberOfSamples;
        const length = Math.min(data.length, totalBytes);
        for (let channel = 0; channel < numberOfChannels; channel++) {
            const buffer = new Int16Array(numberOfSamples);
            ret.push(buffer);
            let sampleI = 0;
            for (let sample = 2 * channel; sample < length; sample += 2 * numberOfChannels) {
                const highByte = data[sample + 1];
                const lowByte = data[sample];
                const sign = highByte & 0x80;
                buffer[sampleI++] = sign
                    ? 0xffff0000 | (highByte << 8) | lowByte
                    : (highByte << 8) | lowByte;
            }
        }
        return ret;
    }
    return [];
}
function multipartDecode(response) {
    const message = new Uint8Array(response);
    const separator = new TextEncoder().encode('\r\n\r\n');
    let offset = 0;
    const maxHeader = 1000;
    let headerEnd = -1;
    for (let i = 0; i < Math.min(message.length - separator.length, offset + maxHeader); i++) {
        let found = true;
        for (let j = 0; j < separator.length; j++) {
            if (message[i + j] !== separator[j]) {
                found = false;
                break;
            }
        }
        if (found) {
            headerEnd = i;
            break;
        }
    }
    if (headerEnd === -1)
        return [response];
    const headerStr = new TextDecoder().decode(message.slice(0, headerEnd));
    const boundaryMatch = headerStr.match(/boundary=([^\s;]+)/i);
    const boundary = boundaryMatch
        ? new TextEncoder().encode(`--${boundaryMatch[1].replace(/"/g, '').trim()}`)
        : null;
    if (!boundary)
        return [response];
    const dataStart = headerEnd + separator.length;
    const components = [];
    let idx = message.indexOf(boundary[0], dataStart);
    while (idx !== -1) {
        const nextSep = message.indexOf(separator[0], idx);
        if (nextSep === -1)
            break;
        const partStart = nextSep + separator.length;
        const nextBound = message.indexOf(boundary[0], partStart);
        const partEnd = nextBound === -1 ? message.length : nextBound - 2;
        if (partEnd > partStart) {
            components.push(response.slice(partStart, partEnd));
        }
        if (nextBound === -1)
            break;
        idx = message.indexOf(boundary[0], nextBound + boundary.length);
    }
    return components.length > 0 ? components : [response];
}
function parseWadoRsImageId(imageId) {
    const uri = imageId.replace(/^wadors:/i, '');
    const studiesIndex = uri.indexOf('/studies/');
    if (studiesIndex === -1)
        return {};
    const wadoRsRoot = uri.substring(0, studiesIndex);
    const afterStudies = uri.substring(studiesIndex + 9);
    const nextSlash = afterStudies.indexOf('/');
    const studyUID = nextSlash !== -1 ? afterStudies.substring(0, nextSlash) : afterStudies;
    return { wadoRsRoot, studyUID };
}
function toArray(seq) {
    if (!seq)
        return [];
    if (Array.isArray(seq))
        return seq;
    if (typeof seq.length === 'number') {
        return Array.from(seq);
    }
    return [seq];
}
export function buildEcgModuleFromInstance(instance, imageId) {
    const raw = instance.WaveformSequence;
    const groups = toArray(raw);
    if (!groups.length)
        return null;
    const group = groups[0];
    const numberOfChannels = group.NumberOfWaveformChannels ?? 0;
    const numberOfSamples = group.NumberOfWaveformSamples ?? 0;
    const samplingFrequency = group.SamplingFrequency ?? 1;
    const bitsAllocated = group.WaveformBitsAllocated ?? 16;
    const sampleInterpretation = group.WaveformSampleInterpretation ?? 'SS';
    const multiplexGroupLabel = group.MultiplexGroupLabel ?? '';
    const channelDefSeq = toArray(group.ChannelDefinitionSequence);
    const channelDefinitionSequence = channelDefSeq.map((ch) => {
        const srcSeqArr = toArray(ch.ChannelSourceSequence);
        const srcSeq = srcSeqArr[0];
        const codeMeaning = srcSeq?.CodeMeaning ?? '';
        return {
            channelSourceSequence: { codeMeaning },
        };
    });
    let waveformDataRaw = (group.WaveformData ?? group.waveformData);
    if (waveformDataRaw &&
        typeof waveformDataRaw.length === 'number' &&
        waveformDataRaw.length > 0) {
        waveformDataRaw = waveformDataRaw[0];
    }
    const waveformData = waveformDataRaw ?? {};
    const { wadoRsRoot = undefined, studyUID = undefined } = imageId
        ? parseWadoRsImageId(imageId)
        : {};
    const retrieveBulkData = async () => {
        const wd = waveformData;
        if (wd instanceof ArrayBuffer ||
            (typeof ArrayBuffer !== 'undefined' &&
                ArrayBuffer.isView &&
                ArrayBuffer.isView(wd))) {
            return convertBuffer(wd, numberOfChannels, numberOfSamples, bitsAllocated, sampleInterpretation);
        }
        if (waveformData.Value)
            return waveformData.Value;
        if (waveformData.InlineBinary) {
            const raw = base64ToUint8Array(waveformData.InlineBinary);
            return convertBuffer(raw, numberOfChannels, numberOfSamples, bitsAllocated, sampleInterpretation);
        }
        if (typeof waveformData.retrieveBulkData === 'function') {
            return waveformData.retrieveBulkData();
        }
        if (waveformData.BulkDataURI) {
            let url = waveformData.BulkDataURI;
            if (url.indexOf(':') === -1 && wadoRsRoot) {
                url = studyUID
                    ? `${wadoRsRoot}/studies/${studyUID}/${url}`
                    : `${wadoRsRoot}/${url}`;
            }
            const response = await fetch(url);
            const buffer = await response.arrayBuffer();
            const contentType = response.headers.get('content-type') || '';
            const decoded = contentType.includes('multipart')
                ? multipartDecode(buffer)[0]
                : buffer;
            return convertBuffer(decoded, numberOfChannels, numberOfSamples, bitsAllocated, sampleInterpretation);
        }
        console.warn('[ecgFromInstance] No waveform data source found. group keys:', Object.keys(group), 'waveformData keys:', Object.keys(waveformData));
        throw new Error('[ecgFromInstance] No waveform data source found');
    };
    return {
        numberOfWaveformChannels: numberOfChannels,
        numberOfWaveformSamples: numberOfSamples,
        samplingFrequency,
        waveformBitsAllocated: bitsAllocated,
        waveformSampleInterpretation: sampleInterpretation,
        multiplexGroupLabel,
        channelDefinitionSequence,
        waveformData: { retrieveBulkData },
    };
}
const ECG_FROM_INSTANCE_PRIORITY = 4_000;
const ecgFromInstanceProvider = (next, query, data, options) => {
    const instance = data;
    const hasWaveform = instance && instance.WaveformSequence;
    if (!hasWaveform) {
        return next(query, data, options);
    }
    const result = buildEcgModuleFromInstance(instance, query);
    return result ?? next(query, data, options);
};
const ECG_AMPLITUDE_INDEX_SIZE = 65536;
const ECG_AMPLITUDE_OFFSET = 32768;
const ecgCalibrationProvider = (next, query, data, options) => {
    const instance = data;
    const raw = instance?.WaveformSequence;
    const groups = toArray(raw);
    if (!groups.length)
        return next(query, data, options);
    const group = groups[0];
    const numberOfWaveformSamples = group.NumberOfWaveformSamples ?? 0;
    const samplingFrequency = group.SamplingFrequency ?? 1;
    const physicalDeltaX = 1 / (samplingFrequency || 1);
    const physicalDeltaY = 0.001;
    return {
        sequenceOfUltrasoundRegions: [
            {
                regionLocationMinX0: 0,
                regionLocationMaxX1: numberOfWaveformSamples,
                regionLocationMinY0: 0,
                regionLocationMaxY1: ECG_AMPLITUDE_INDEX_SIZE - 1,
                referencePixelX0: 0,
                referencePixelY0: ECG_AMPLITUDE_OFFSET,
                physicalDeltaX,
                physicalDeltaY,
                physicalUnitsXDirection: 4,
                physicalUnitsYDirection: -1,
                regionDataType: 1,
            },
        ],
    };
};
export function registerEcgFromInstanceProvider() {
    addTypedProvider(MetadataModules.ECG, ecgFromInstanceProvider, {
        priority: ECG_FROM_INSTANCE_PRIORITY,
    });
    addTypedProvider(MetadataModules.CALIBRATION, ecgCalibrationProvider, {
        priority: ECG_FROM_INSTANCE_PRIORITY,
    });
}
